import { Injectable } from '@angular/core';
import { CommunicationService } from '../../http/communication.service';
import { catchError, Observable } from 'rxjs';
import { Account, AccountPayload, AccountUsersResponse, AiTopicsResponse, Brand, BrandLogoResponse, BrandPayload, BrandResearchStatus, BrandsResponse, CheckDomainConflictsPayload, DomainConflictsResponse, LinkedinTermRecord, ManagementRequest, MetaPageRecord, MetaPageValidationResult, ResearchMemory, ResearchRun, User, UserPayload, UserResponse } from './types';
import { AdvertiserDomainsResponse } from '../../services/types';

/**
 * Data-access (repository) layer for the management screen — pure HTTP, no state. Cross-tab state and
 * caching live in ManagementStore; this service is also used directly for one-off, modal-specific
 * reads (account users, domain search, logo, meta-page validation) and by the auth guard.
 */
@Injectable({
  providedIn: 'root'
})
export class ManagementService {

  constructor(private communicationService: CommunicationService) { }

  public getUserRoles(): Observable<{ roles: string[] }> {
    return this.communicationService.httpGet<{ roles: string[] }>('/users-dashboard/constants').pipe(
      catchError((error) => {
        console.error('Error fetching user roles', error);
        throw error;
      }
      ));
  }

  public getUsers(params: Partial<ManagementRequest>): Observable<UserResponse> {
    return this.communicationService.httpGet<UserResponse>('/users/paginated', { params }).pipe(
      catchError((error) => {
        console.error('Error fetching users', error);
        throw error;
      }
      ));
  }

  public addUser(payload: UserPayload): Observable<User> {
    return this.communicationService.httpPost<User>('/users', payload).pipe(
      catchError((error) => {
        console.error('Error adding user', error);
        throw error;
      }
      ));
  }

  public editUser(payload: UserPayload): Observable<User> {
    return this.communicationService.httpPut<User>(`/users/${payload.id}`, payload).pipe(
      catchError((error) => {
        console.error('Error editing user', error);
        throw error;
      }
      ));
  }

  public deleteUser(userId: string): Observable<User> {
    return this.communicationService.httpDelete<User>(`/users/${userId}`).pipe(
      catchError((error) => {
        console.error('Error deleting user', error);
        throw error;
      }
      ));
  }

  public resendInvite(email: string): Observable<void> {
    return this.communicationService.httpPost<void>('/users/resend-invite', { email }).pipe(
      catchError((error) => {
        console.error('Error resending invite', error);
        throw error;
      }
      ));
  }

  public getBrands(params: Partial<ManagementRequest>): Observable<BrandsResponse> {
    return this.communicationService.httpGet<BrandsResponse>('/brands', { params }).pipe(
      catchError((error) => {
        console.error('Error fetching brands', error);
        throw error;
      }
      ));
  }

  public getAccounts(params: Partial<ManagementRequest>): Observable<{ accounts: Account[]; total: number }> {
    return this.communicationService.httpGet<{ accounts: Account[]; total: number }>('/accounts', { params }).pipe(
      catchError((error) => {
        console.error('Error fetching accounts', error);
        throw error;
      }
      ));
  }

  public addAccount(payload: AccountPayload): Observable<Account> {
    return this.communicationService.httpPost<Account>('/accounts', payload).pipe(
      catchError((error) => {
        console.error('Error adding account', error);
        throw error;
      }
      ));
  }

  public updateAccountSubscription(accountKey: string, payload: { action?: 'convert'; trialEndsAt?: string }): Observable<Account> {
    return this.communicationService.httpPut<Account>(`/accounts/${accountKey}/subscription`, payload).pipe(
      catchError((error) => {
        console.error('Error updating account subscription', error);
        throw error;
      }
      ));
  }

  public editAccount(payload: AccountPayload): Observable<Account> {
    return this.communicationService.httpPut<Account>(`/accounts/${payload.accountKey}`, payload).pipe(
      catchError((error) => {
        console.error('Error editing account', error);
        throw error;
      }
      ));
  }

  public deleteAccount(accountKey: string): Observable<Account> {
    return this.communicationService.httpDelete<Account>(`/accounts/${accountKey}`).pipe(
      catchError((error) => {
        console.error('Error deleting account', error);
        throw error;
      }
      ));
  }

  public getAccountUsers(accountKey: string): Observable<AccountUsersResponse> {
    return this.communicationService.httpGet<AccountUsersResponse>(`/accounts/users/${accountKey}`,).pipe(
      catchError((error) => {
        console.error('Error fetching account users', error);
        throw error;
      }
      ));
  }

  public searchAdvertisersDomains(query: string): Observable<AdvertiserDomainsResponse> {
    return this.communicationService.httpGet<AdvertiserDomainsResponse>(`/advertiser-domains/search/${query}`,).pipe(
      catchError((error) => {
        console.error('Error searching advertiser domains', error);
        throw error;
      }
      ));
  }

  public checkDomainConflicts(payload: CheckDomainConflictsPayload): Observable<DomainConflictsResponse> {
    return this.communicationService.httpPost<DomainConflictsResponse>('/brands/check-domain-conflicts', payload).pipe(
      catchError((error) => {
        console.error('Error checking domain conflicts', error);
        throw error;
      })
    );
  }

  public addBrand(payload: BrandPayload): Observable<Brand> {
    return this.communicationService.httpPost<Brand>('/brands', payload).pipe(
      catchError((error) => {
        console.error('Error adding brand', error);
        throw error;
      }
      ));
  }

  public editBrand(payload: BrandPayload): Observable<Brand> {
    return this.communicationService.httpPut<Brand>(`/brands/${payload.brandKey}`, payload).pipe(
      catchError((error) => {
        console.error('Error editing brand', error);
        throw error;
      }
      ));
  }

  public validateMetaPage(pageId: string, countries: string[]): Observable<MetaPageValidationResult> {
    return this.communicationService.httpPost<MetaPageValidationResult>('/brands/meta-pages/validate', { pageId, countries }).pipe(
      catchError((error) => {
        console.error('Error validating meta page', error);
        throw error;
      }
      ));
  }

  public getBrandMetaPages(brandKey: string): Observable<MetaPageRecord[]> {
    return this.communicationService.httpGet<MetaPageRecord[]>(`/brands/${brandKey}/meta-pages`).pipe(
      catchError((error) => {
        console.error('Error fetching brand meta pages', error);
        throw error;
      }
      ));
  }

  public getMetaPageCountries(): Observable<{ code: string; name: string }[]> {
    return this.communicationService.httpGet<{ code: string; name: string }[]>('/brands/meta-pages/countries').pipe(
      catchError((error) => {
        console.error('Error fetching meta page countries', error);
        throw error;
      }
      ));
  }

  public getBrandLinkedinTerms(brandKey: string): Observable<LinkedinTermRecord[]> {
    return this.communicationService.httpGet<LinkedinTermRecord[]>(`/brands/${brandKey}/linkedin-terms`).pipe(
      catchError((error) => {
        console.error('Error fetching brand linkedin terms', error);
        throw error;
      }
      ));
  }

  public getBrandResearch(brandKey: string): Observable<BrandResearchStatus> {
    return this.communicationService.httpGet<BrandResearchStatus>(`/brands/${encodeURIComponent(brandKey)}/research`).pipe(
      catchError((error) => {
        console.error('Error fetching brand research status', error);
        throw error;
      })
    );
  }

  public getBrandResearchMemory(brandKey: string, country: string): Observable<ResearchMemory> {
    return this.communicationService.httpGet<ResearchMemory>(`/brands/${encodeURIComponent(brandKey)}/research/memory`, { params: { country } }).pipe(
      catchError((error) => {
        console.error('Error fetching brand research memory', error);
        throw error;
      })
    );
  }

  public triggerBrandResearch(brandKey: string, validFrom?: string): Observable<ResearchRun> {
    return this.communicationService.httpPost<ResearchRun>(`/brands/${encodeURIComponent(brandKey)}/research-runs`, validFrom ? { validFrom } : {}).pipe(
      catchError((error) => {
        console.error('Error triggering brand research', error);
        throw error;
      })
    );
  }

  public findBrandLogo(brandName: string): Observable<BrandLogoResponse> {
    return this.communicationService.httpPost<BrandLogoResponse>('/brands/logo/find', { brandName }).pipe(
      catchError((error) => {
        console.error('Error finding brand logo', error);
        throw error;
      })
    );
  }

  public uploadBrandLogo(brandName: string, logoFile: File): Observable<BrandLogoResponse> {
    const formData = new FormData();
    formData.append('file', logoFile);
    formData.append('brandName', brandName);

    return this.communicationService.httpPost<BrandLogoResponse>(`/brands/logo/upload`, formData).pipe(
      catchError((error) => {
        console.error('Error uploading brand logo', error);
        throw error;
      })
    );
  }

  public getAiTopics(): Observable<AiTopicsResponse> {
    return this.communicationService.httpGet<AiTopicsResponse>('/ai-topics').pipe(
      catchError((error) => {
        console.error('Error fetching ai topics', error);
        throw error;
      })
    );
  }
}
